import { useEffect, useState } from "react";
import PricingHeader from "./components/headerPricing";
import "./pricing.css";
import { useLoaderData, useNavigate, useNavigation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ENTERPRISE_PORTAL_SIGNIN_URL,
  ENTERPRISE_PORTAL_SIGNUP_URL,
} from "../../constants";
import Swal from "sweetalert2";
import apiClient from "../../axios";
import PricingTable from "./components/pricingTable";

// Define types for our data
interface Theme {
  id: number;
  name: string;
  code: string;
  levelId: number | null;
}

interface Level {
  id: number;
  name: string;
}

interface EmployeeRange {
  id: number;
  name: string;
}

interface PriceAdjustment {
  rangeId: number;
  adjustment: number;
}

interface ThemeDetail {
  priceAdjustments?: PriceAdjustment[];
}

interface ThemeDetails {
  [key: number]: ThemeDetail;
}

interface Combination {
  id: number;
  planId: number;
  themeIds: number[];
  price: number;
  partnerUnitPriceFloor?: number | null;
  partnerPriceFloorFrom?: number | null;
  themeDetails: ThemeDetails;
}

interface Feature {
  name?: string;
  en_name?: string;
  disabled?: boolean;
}

interface PlanData {
  combinationId: number;
  timestamp: number;
  themes: string[];
  mode: string;
  count?: number;
  partnerSizes?: Record<string, number>;
  level?: number;
  employeeRange?: number;
}

interface Plan {
  id: number;
  name: string;
  type: string;
  price?: number;
  features: Feature[];
}

interface PricingData {
  levels: Level[];
  themes: Theme[];
  plans: Plan[];
  combinations: Combination[];
  employeeRanges: EmployeeRange[];
}

// Skeleton loading components
const ThemeSkeleton = () => (
  <div className="flex gap-3 items-center p-4 cursor-not-allowed border rounded-lg w-64 flex-none bg-white animate-pulse">
    <div className="w-6 h-6 rounded-full bg-gray-200"></div>
    <div className="h-5 bg-gray-200 rounded w-32"></div>
  </div>
);

// Improved skeleton loading components
const PlanSkeleton = ({ isMobile = false }) => {
  // Mobile view skeleton
  if (isMobile) {
    return (
      <div className="relative rounded-xl overflow-hidden shadow-lg border border-gray-200 animate-pulse">
        {/* Plan header */}
        <div className="p-6 bg-white">
          <div className="h-7 bg-gray-200 rounded w-40 mb-3"></div>
          <div className="h-9 bg-gray-200 rounded w-24"></div>
        </div>
        
        {/* Features */}
        <div className="px-6 py-4 bg-white">
          <div className="h-4 bg-gray-200 rounded w-40 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-gray-200"></div>
                <div className="h-4 bg-gray-200 rounded w-full ml-3"></div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Button */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
          <div className="h-12 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }
  
  // Desktop table view skeleton
  return (
    <div className="overflow-hidden border border-gray-200 rounded-lg shadow-lg animate-pulse">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            {/* Empty cell for feature column header */}
            <th scope="col" className="w-1/3 px-6 py-6 bg-gray-50"></th>
            
            {/* Plan column headers - showing three plan columns */}
            {[1, 2, 3].map((plan) => (
              <th key={plan} scope="col" className="px-6 py-6 text-center bg-gray-50">
                <div className="flex flex-col items-center">
                  {plan === 2 && <div className="h-6 bg-gray-200 rounded-full w-24 mb-2"></div>}
                  <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-20 mt-2"></div>
                  <div className="h-10 bg-gray-200 rounded w-full mt-4"></div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        
        <tbody className="bg-white divide-y divide-gray-200">
          {/* Feature rows - showing five feature rows */}
          {[1, 2, 3, 4, 5].map((featureIndex) => (
            <tr key={featureIndex} className={featureIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              {/* Feature name */}
              <td className="px-6 py-4">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </td>
              
              {/* Feature availability per plan */}
              {[1, 2, 3].map((plan) => (
                <td key={plan} className="px-6 py-4 text-center">
                  <div className="h-5 w-5 bg-gray-200 rounded-full mx-auto"></div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Usage in the PricingPage component
const PricingSkeletonSection = () => {
  // Get screen size to determine which skeleton to show
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);
  
  return isMobile ? (
    // Mobile view - stacked cards
    <div className="space-y-6">
      <PlanSkeleton isMobile={true} />
      <PlanSkeleton isMobile={true} />
      <PlanSkeleton isMobile={true} />
    </div>
  ) : (
    // Desktop view - table layout
    <PlanSkeleton isMobile={false} />
  );
};

function PricingPage() {
  // Get initial data from loader
  const initialPricingData = useLoaderData() as PricingData;
  const navigation = useNavigation();
  const isRouteLoading = navigation.state === "loading";
  
  // State for actual pricing data
  const [pricingData, setPricingData] = useState<PricingData>(initialPricingData);
  const [isDataLoading, setIsDataLoading] = useState(
    initialPricingData.levels.length === 0 && 
    initialPricingData.themes.length === 0
  );
  
  // Determine if we're in any loading state
  const isLoading = isRouteLoading || isDataLoading;
  
  // Use actual data
  const pricing = pricingData;
  
  // Translation hook
  const { t } = useTranslation("global");
  
  // Fetch full data if we received empty data from loader
  useEffect(() => {
    if (initialPricingData.levels.length === 0 && initialPricingData.themes.length === 0) {
      setIsDataLoading(true);
      
      apiClient.get("/billing/plan-list/combinations")
        .then(res => {
          setPricingData(res.data);
          setIsDataLoading(false);
        })
        .catch(error => {
          console.error("Error fetching pricing data:", error);
          setIsDataLoading(false);
        });
    }
  }, [initialPricingData]);
  
  // State for supplier count (partners)
  const [isRadioSelected, setIsRadioSelected] = useState(false);
  const [supplierCount, setSupplierCount] = useState("");
  const [isValid, setIsValid] = useState(true);
  
  // State for levels and employee count
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [employeeCount, setEmployeeCount] = useState<string>("");
  const [employeeRangeId, setEmployeeRangeId] = useState<number | null>(null);
  const [isEmployeeCountValid, setIsEmployeeCountValid] = useState(true);
  const [specialPricingThemes] = useState(["RSE_LVL2"]); // Themes that need employee count
  const [availableThemes, setAvailableThemes] = useState<Theme[]>([]);
  
  // State to track if RSE theme is selected
  const [isRseSelected, setIsRseSelected] = useState(false);

  const [partnerSizeRanges, setPartnerSizeRanges] = useState({
    small: "", // 1-9
    medium: "", // 10-49
    large: "", // 50-250
    enterprise: "" // 250+
  });

  const [isPartnerSizeRangesValid, setIsPartnerSizeRangesValid] = useState({
    small: true,
    medium: true,
    large: true,
    enterprise: true
  });

  /*hooks*/
  const navigate = useNavigate();
  const [selectedThemes, setSelectedThemes] = useState<Theme[]>([]);
  const [mode, setMode] = useState("AUTO_EVALUATION");
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlan, setLoadingPlan] = useState({ id: -1 });

  // Set default level when data is loaded
  useEffect(() => {
    if (!isLoading && pricing.levels && pricing.levels.length > 0 && !selectedLevel) {
      setSelectedLevel(pricing.levels[0].id);
    }
  }, [isLoading, pricing.levels, selectedLevel]);

  useEffect(() => {
    // Filter themes based on selected level
    if (selectedLevel && pricing.themes) {
      const filteredThemes = pricing.themes.filter(theme => 
        theme.levelId === selectedLevel || theme.levelId === null
      );
      setAvailableThemes(filteredThemes);
      
      // Update selected themes to only include themes available at this level
      const newSelectedThemes = selectedThemes.filter(selected => 
        filteredThemes.some(theme => theme.id === selected.id)
      );
      
      // If we lost some selected themes due to level change, add some default ones
      if (newSelectedThemes.length === 0 && filteredThemes.length > 0) {
        newSelectedThemes.push(filteredThemes[0]);
      }
      
      setSelectedThemes(newSelectedThemes);
    } else {
      setAvailableThemes(pricing.themes || []);
    }
  }, [selectedLevel, pricing.themes]);

  // Initialize selected themes when available themes change
  useEffect(() => {
    if (availableThemes.length > 0 && selectedThemes.length === 0) {
      setSelectedThemes([availableThemes[0]]);
    }
  }, [availableThemes]);
  
  // Update plans when dependencies change
  useEffect(() => {
    if (selectedThemes.length > 0) {
      setPlans(checkPlans());
    }
    
    // Check if RSE theme is selected
    const hasRseTheme = selectedThemes.some(theme => 
      specialPricingThemes.includes(theme.code)
    );
    
    if (hasRseTheme && !isRseSelected && supplierCount && selectedLevel === 2 && mode === "PARTNER") {
      const currentCount = parseInt(supplierCount) || 0;
      if (currentCount > 0) {
        setPartnerSizeRanges({
          small: currentCount.toString(),  // Put all partners in the small category
          medium: "0",                     // Zero out other categories
          large: "0",
          enterprise: "0"
        });
      }
    }
    setIsRseSelected(hasRseTheme);
  }, [mode, selectedLevel, employeeRangeId, partnerSizeRanges, selectedThemes]);

  const checkPlans = function () {
    let plans: Plan[] = [];
    pricing.plans.forEach((plan) => {
      const price = calculatePrice(plan.id);
      plan.price = price;
      plans.push(plan);
    });
    return plans;
  };

  // At the beginning of the PricingPage function
useEffect(() => {
  // Only run after data is loaded and no selections have been made yet
  if (!isLoading && pricing.levels && pricing.themes && !selectedLevel) {
    try {
      // Get preferences from localStorage
      const preferencesString = localStorage.getItem('pricingPreferences');
      
      if (preferencesString) {
        const preferences = JSON.parse(preferencesString);
        
        // Check if preferences are recent (e.g., within the last hour)
        const isRecent = Date.now() - preferences.timestamp < 3600000; // 1 hour
        
        if (isRecent) {
          // Set level if valid
          if (preferences.level && pricing.levels.some(level => level.id === preferences.level)) {
            setSelectedLevel(preferences.level);
            
            // We need to wait for availableThemes to update based on the selected level
            setTimeout(() => {
              // Find and select the theme
              if (preferences.theme) {
                const availableThemes = pricing.themes.filter(theme => 
                  theme.levelId === preferences.level || theme.levelId === null
                );
                
                const themeToSelect = availableThemes.find(theme => 
                  theme.code === preferences.theme || theme.name.includes(preferences.theme)
                );
                
                if (themeToSelect) {
                  setSelectedThemes([themeToSelect]);
                }
              }
              
              // Clear the preferences after using them
              localStorage.removeItem('pricingPreferences');
            }, 100);
          }
        } else {
          // Preferences are too old, remove them
          localStorage.removeItem('pricingPreferences');
        }
      }
    } catch (error) {
      console.error('Error parsing preferences:', error);
      localStorage.removeItem('pricingPreferences');
    }
  }
}, [isLoading, pricing.levels, pricing.themes]);

  const handlePartnerSizeRangeChange = (range: string, value: string) => {
    const numValue = Number(value);
    const oldValue = Number(partnerSizeRanges[range as keyof typeof partnerSizeRanges]) || 0;
    
    // Validate input (must be between 0 and 1000)
    const isValid = numValue >= 0 && numValue <= 1000;
    
    if (!isValid) {
      // Handle invalid input
      setIsPartnerSizeRangesValid(prev => ({
        ...prev,
        [range]: false
      }));
      return;
    }
    
    // Calculate the difference between new and old value
    const difference = numValue - oldValue;
    
    // Calculate the current total from all ranges
    const currentTotal = Object.values(partnerSizeRanges)
      .reduce((sum, val) => sum + (Number(val) || 0), 0);
    
    // If total would be negative after change, prevent the change
    if (currentTotal + difference < 0) {
      return;
    }
    
    // Create a new partnerSizeRanges object with the changed value
    const newRanges = {
      ...partnerSizeRanges,
      [range]: numValue.toString()
    };
    
    // Get the next range to adjust (to maintain the total)
    let nextRange: string | null = null;
    if (range === "small") nextRange = "medium";
    else if (range === "medium") nextRange = "large";
    else if (range === "large") nextRange = "enterprise";
    else if (range === "enterprise") nextRange = "small";
    
    // If the difference is negative (reducing a value), add it to the next range
    if (difference < 0 && nextRange) {
      const nextRangeValue = Number(newRanges[nextRange as keyof typeof newRanges]) || 0;
      const adjustedValue = nextRangeValue - difference; // subtract negative value = add
      newRanges[nextRange as keyof typeof newRanges] = adjustedValue.toString();
    } 
    // If the difference is positive, subtract from the next range (if possible)
    else if (difference > 0 && nextRange) {
      const nextRangeValue = Number(newRanges[nextRange as keyof typeof newRanges]) || 0;
      if (nextRangeValue >= difference) {
        // Only subtract if the next range has enough value
        const adjustedValue = nextRangeValue - difference;
        newRanges[nextRange as keyof typeof newRanges] = adjustedValue.toString();
      } else {
        // Not enough in the next range, try to distribute across all other ranges
        let remainingDifference = difference;
        const otherRanges = ["small", "medium", "large", "enterprise"].filter(r => r !== range);
        
        // First pass: calculate how much we can take from other ranges
        const availableValues = otherRanges.map(r => Number(newRanges[r as keyof typeof newRanges]) || 0);
        const totalAvailable = availableValues.reduce((sum, val) => sum + val, 0);
        
        // If we can't take enough from other ranges, limit the change
        if (totalAvailable < difference) {
          const maxPossibleValue = oldValue + totalAvailable;
          newRanges[range] = maxPossibleValue.toString();
          remainingDifference = totalAvailable;
        }
        
        // Distribute the remaining difference proportionally across other ranges
        if (remainingDifference > 0 && totalAvailable > 0) {
          otherRanges.forEach((r, index) => {
            const rangeValue = Number(newRanges[r as keyof typeof newRanges]) || 0;
            // Proportional reduction based on value in this range
            const reduction = Math.min(
              rangeValue, 
              Math.ceil((rangeValue / totalAvailable) * remainingDifference)
            );
            
            newRanges[r as keyof typeof newRanges] = (rangeValue - reduction).toString();
          });
        }
      }
    }
    
    // Update partner size ranges
    setPartnerSizeRanges(newRanges);
    
    // Update validation state
    setIsPartnerSizeRangesValid(prev => ({
      ...prev,
      [range]: isValid
    }));
    
    // The total count should remain the same, but calculate it again just to be safe
    const newTotal = Object.values(newRanges)
      .reduce((sum, val) => sum + (Number(val) || 0), 0);
    
    // Set the supplier count
    setSupplierCount(newTotal.toString());
    
    // Force plans to update
    setPlans(checkPlans());
  };

  const calculatePrice = function (idPlan: number) {
    let price = 0;
  
    const combinations = pricing.combinations.filter((combin) => {
      return (
        combin.planId == idPlan &&
        combin.themeIds.length == selectedThemes.length
      );
    });
  
    for (const combin of combinations) {
      if (isMatchedIds(selectedThemes, combin.themeIds)) {
        price = combin.price;
        
        // Check if we have employee range selected and need to apply adjustments
        if (employeeRangeId) {
          // Loop through each selected theme
          for (const theme of selectedThemes) {
            // Check if this theme has special pricing (like RSE)
            if (specialPricingThemes.includes(theme.code)) {
              // Get the theme details from the combination
              const themeDetail = combin.themeDetails[theme.id];
              
              if (themeDetail && themeDetail.priceAdjustments && themeDetail.priceAdjustments.length > 0) {
                // Find the applicable range adjustment
                const adjustment = themeDetail.priceAdjustments.find(adj => 
                  adj.rangeId === employeeRangeId
                );
                
                if (adjustment) {
                  price += adjustment.adjustment;
                }
              }
            }
          }
        }
        
        return price;
      }
    }
  
    return price;
  }

  const isMatchedIds = function (themes: Theme[], ids2: number[]) {
    let isMatchedIds = true;
    for (let i = 0; i < themes.length; i++) {
      let themeId = themes[i].id;
      let isFound = false;

      for (let j = 0; j < ids2.length; j++) {
        let id = ids2[j];

        if (themeId == id) {
          isFound = true;
          break;
        }
      }
      if (!isFound) {
        isMatchedIds = false;
        break;
      }
    }
    return isMatchedIds;
  };
  
  const getCombinationId = (plan: Plan) => {
    let combinId = -1;

    const combinations = pricing.combinations.filter((combin) => {
      return (
        combin.planId == plan.id &&
        combin.themeIds.length == selectedThemes.length
      );
    });

    combinations.forEach((combin) => {
      if (isMatchedIds(selectedThemes, combin.themeIds)) {
        combinId = combin.id;
        return;
      }
    });

    return combinId;
  };

  // This function should now always return false to never show the employee count section
  const shouldShowEmployeeCount = () => {
    return (
      selectedLevel === 2 && // Level 2 is selected
      mode === "AUTO_EVALUATION" && // TPE-PME mode is selected
      isRseSelected // RSE theme is selected
    );
  };

  // Check if we should show the partner size breakdown
  const shouldShowPartnerSizeBreakdown = () => {
    return (
      selectedLevel === 2 && 
      mode === "PARTNER" && 
      isRseSelected
    );
  };

  /*events functions*/
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const value = Number(inputValue);
    
    // Handle empty input case
    if (inputValue === "") {
      setSupplierCount("");
      setIsValid(false);
      
      // Clear all partner size ranges when main input is empty
      if (selectedLevel === 2 && isRseSelected) {
        setPartnerSizeRanges({
          small: "",
          medium: "",
          large: "",
          enterprise: ""
        });
      }
      return true;
    }
    
    // Handle invalid values (0 or >1000)
    if (value === 0 || value > 1000) {
      setSupplierCount("");
      setIsValid(false);
      
      // Clear all partner size ranges when input is invalid
      if (selectedLevel === 2 && isRseSelected) {
        setPartnerSizeRanges({
          small: "",
          medium: "",
          large: "",
          enterprise: ""
        });
      }
      return true;
    }
    
    // Valid input case
    setSupplierCount(value.toString());
    setIsValid(true);
    
    // If in level 2 with RSE theme, put all value in the small category and set others to 0
    if (selectedLevel === 2 && isRseSelected) {
      setPartnerSizeRanges({
        small: value.toString(),  // All partners go to the 1-9 category
        medium: "0",              // Set others to "0" instead of empty string
        large: "0",
        enterprise: "0"
      });
    }
  };

  const handleEmployeeCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = Number(e.target.value);
    if (value === 0 || value > 10000) {
      setEmployeeCount("");
      setIsEmployeeCountValid(false);
      return true;
    }
    setEmployeeCount(value.toString());
    setIsEmployeeCountValid(true);
  };

  const onChoosePlan = async (plan: Plan) => {
    let errorMessage = null;
      
    if (mode === "PARTNER") {
      if (supplierCount === "") {
        errorMessage = "Le nombre de partenaires ne peut pas être vide !";
      }
        
      if (shouldShowEmployeeCount() && employeeRangeId === null) {
        errorMessage = "Veuillez sélectionner une tranche d'employés";
      }
    }
      
    if (errorMessage) {
      await Swal.fire({
        text: errorMessage,
        icon: "error",
      });
      return;
    }
    
    const result = await Swal.fire({
      title: t("sweetAlert.title"),
      text: t("sweetAlert.text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("sweetAlert.confirmButtonText"),
      cancelButtonText: t("sweetAlert.cancelButtonText"),
    });
    
    if (result.isConfirmed) {
      // Create the data object to store
      const planData: PlanData = {
        combinationId: getCombinationId(plan),
        timestamp: Date.now(),
        themes: selectedThemes.map(theme => theme.code),
        mode: mode
      };
        
      // Add partner data if in PARTNER mode
      if (mode === "PARTNER" && supplierCount) {
        planData.count = Number(supplierCount) || 0;
            
        // Add partner size breakdown if using RSE in Level 2
        if (shouldShowPartnerSizeBreakdown()) {
          planData.partnerSizes = {};
          Object.entries(partnerSizeRanges).forEach(([range, count]) => {
            if (count !== "") {
              planData.partnerSizes[range] = Number(count);
            }
          });
        }
      }
        
      // Add level if selected
      if (selectedLevel) {
        planData.level = selectedLevel;
      }
        
      // Add employee range if needed
      if (shouldShowEmployeeCount() && employeeRangeId) {
        planData.employeeRange = employeeRangeId;
      }
        
      // Store the data in localStorage
      localStorage.setItem('selectedPlanData', JSON.stringify(planData));
        
      // Navigate to the enterprise portal
      const baseUrl = mode === "PARTNER" ? ENTERPRISE_PORTAL_SIGNIN_URL : ENTERPRISE_PORTAL_SIGNIN_URL;
      window.location.href = baseUrl;
    }
  };

  const calculatePartnerPrice = (plan: Plan) => {
    let basePrice = plan.price || 0;
    let finalPrice = basePrice;
    
    if (plan?.type === "PARTNER") {
      if (supplierCount) {
        // Start with the base calculation
        finalPrice = 1000 + basePrice * Number(supplierCount);
        
        // Add adjustments for RSE theme based on partner size distribution
        if (isRseSelected && selectedLevel === 2 && shouldShowPartnerSizeBreakdown()) {
          // Get the combination for this plan
          const combinationId = getCombinationId(plan);
          const combination = pricing.combinations.find(c => c.id === combinationId);
          
          if (combination) {
            // Find the RSE theme in the selected themes
            const rseTheme = selectedThemes.find(theme => 
              theme.code === "RSE_LVL2" && theme.levelId === 2
            );
            
            if (rseTheme && combination.themeDetails[rseTheme.id]) {
              const themeDetail = combination.themeDetails[rseTheme.id];
              
              if (themeDetail.priceAdjustments && themeDetail.priceAdjustments.length > 0) {
                // Calculate adjustments based on partner size ranges
                let totalAdjustments = 0;
                
                // Small partners (1-9 employees)
                if (partnerSizeRanges.small) {
                  const smallCount = Number(partnerSizeRanges.small) || 0;
                  const smallAdjustment = themeDetail.priceAdjustments.find(adj => adj.rangeId === 1);
                  if (smallAdjustment) {
                    totalAdjustments += smallCount * smallAdjustment.adjustment;
                  }
                }
                
                // Medium partners (10-49 employees)
                if (partnerSizeRanges.medium) {
                  const mediumCount = Number(partnerSizeRanges.medium) || 0;
                  const mediumAdjustment = themeDetail.priceAdjustments.find(adj => adj.rangeId === 2);
                  if (mediumAdjustment) {
                    totalAdjustments += mediumCount * mediumAdjustment.adjustment;
                  }
                }
                
                // Large partners (50-250 employees)
                if (partnerSizeRanges.large) {
                  const largeCount = Number(partnerSizeRanges.large) || 0;
                  const largeAdjustment = themeDetail.priceAdjustments.find(adj => adj.rangeId === 3);
                  if (largeAdjustment) {
                    totalAdjustments += largeCount * largeAdjustment.adjustment;
                  }
                }
                
                // Enterprise partners (250+ employees)
                if (partnerSizeRanges.enterprise) {
                  const enterpriseCount = Number(partnerSizeRanges.enterprise) || 0;
                  const enterpriseAdjustment = themeDetail.priceAdjustments.find(adj => adj.rangeId === 4);
                  if (enterpriseAdjustment) {
                    totalAdjustments += enterpriseCount * enterpriseAdjustment.adjustment;
                  }
                }
                
                // Add total adjustments to the final price
                finalPrice += totalAdjustments;
              }
            }
          }
        }
      }
    }
    
    const partnerCount = Number(supplierCount);
    const combinationId = getCombinationId(plan);
    const matchedCombination = pricing.combinations.find(c => c.id === combinationId);
    if (
      matchedCombination?.partnerUnitPriceFloor != null &&
      matchedCombination?.partnerPriceFloorFrom != null &&
      partnerCount >= matchedCombination.partnerPriceFloorFrom
    ) {
      finalPrice = Math.max(
        finalPrice,
        partnerCount * matchedCombination.partnerUnitPriceFloor
      );
    }

    return finalPrice;
  };

  const onThemeClick = (theme: Theme) => {
    let isAlreadyExist = selectedThemes.some((item) => item.id == theme.id);
  
    if (isAlreadyExist) {
      if (selectedThemes.length > 1) {
        let newList = selectedThemes.filter((item) => item.id != theme.id);
        setSelectedThemes(newList);
        
        // If removing the RSE theme, also clear employee range selection if in TPE-PME mode
        if (specialPricingThemes.includes(theme.code) && mode === "AUTO_EVALUATION") {
          setEmployeeRangeId(null);
        }
      }
    } else {
      const newList = [...selectedThemes, theme];
      setSelectedThemes(newList);
      
      // If adding RSE theme and in level 2 in TPE-PME mode, auto-select "1 - 9 employés"
      if (specialPricingThemes.includes(theme.code) && selectedLevel === 2 && mode === "AUTO_EVALUATION") {
        setEmployeeRangeId(1); // Assuming ID 1 is "1 - 9 employés"
      }
    }
  };

  const onChooseMode = (mode: string) => {
    setMode(mode);
    setIsRadioSelected(mode === "PARTNER");
    
    // If switching to TPE-PME mode with RSE theme selected at level 2, auto-select "1 - 9 employés"
    if (mode === "AUTO_EVALUATION" && isRseSelected && selectedLevel === 2) {
      setEmployeeRangeId(1); // Assuming ID 1 is "1 - 9 employés"
    }
  };
  
  const onChooseLevel = (levelId: number) => {
    setSelectedLevel(levelId);
    
    // If selecting level 2 with RSE theme in TPE-PME mode, auto-select "1 - 9 employés"
    if (levelId === 2 && isRseSelected && mode === "AUTO_EVALUATION") {
      setEmployeeRangeId(1); // Assuming ID 1 is "1 - 9 employés"
    }
  };

  // Define translation keys for partner size ranges
  const partnerSizeLabels = {
    small: "1-9 employés",
    medium: "10-49 employés",
    large: "50-250 employés",
    enterprise: "250+ employés"
  };

  return (
    <div>
      <PricingHeader />
      <h1 className="mt-14 text-center mx-20 text-black text-lg mb-10">
        {t("home.headerPricing")}
      </h1>

      {/* Main pricing card */}
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Mode selection */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-center mb-4">{t("pricing.chooseEvaluationMode")}</h2>
          <div className="flex justify-center space-x-4">
            <ModeItem
              name={t("pricing.tpePme")}
              mode="AUTO_EVALUATION"
              onClick={onChooseMode}
              isSelected={mode == "AUTO_EVALUATION"}
            />
            <ModeItem
              name={t("pricing.largeAccounts")}
              mode="PARTNER"
              onClick={onChooseMode}
              isSelected={mode == "PARTNER"}
            />
          </div>
        </div>

        {/* Level tabs */}
        <div className="border-b border-gray-200">
          <div className="flex justify-center">
            {isLoading ? (
              // Skeleton loading for level tabs
              <>
                <div className="px-6 py-4 w-32">
                  <div className="h-6 bg-gray-200 rounded w-full animate-pulse"></div>
                </div>
                <div className="px-6 py-4 w-32">
                  <div className="h-6 bg-gray-200 rounded w-full animate-pulse"></div>
                </div>
              </>
            ) : (
              pricing.levels && pricing.levels.length > 0 && 
              pricing.levels.map(level => (
                <button
                  key={level.id}
                  onClick={() => onChooseLevel(level.id)}
                  className={`px-6 py-4 text-lg font-medium relative ${
                    selectedLevel === level.id 
                      ? 'text-green-500' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {level.name}
                  {selectedLevel === level.id && (
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-green-500"></div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Card content */}
        <div className="p-6">
          {/* Theme selection */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-center">
              {t("home.choixTheme")}
            </h2>
            <div 
              className={`flex ${
                // Only prevent wrapping when at level 2 and exactly 4 themes
                selectedLevel === 2 && availableThemes.length === 4 
                  ? 'flex-nowrap justify-center overflow-x-auto' 
                  : 'flex-wrap justify-center'
              } gap-4`}
            >
              {isLoading ? (
                // Skeleton loading for themes
                <>
                  <ThemeSkeleton />
                  <ThemeSkeleton />
                  <ThemeSkeleton />
                </>
              ) : (
                availableThemes.map((theme) => (
                  <ThemeItem
                    key={theme.id}
                    onClick={onThemeClick}
                    isSelcted={selectedThemes.some(selected => selected.id === theme.id)}
                    theme={theme}
                    smallerWidth={selectedLevel === 2 && availableThemes.length === 4}
                  />
                ))
              )}
            </div>
          </div>

          {/* Employee count range selection */}
          {shouldShowEmployeeCount() && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-center">
                {t("pricing.employeeCount")}
              </h2>
              <div className="flex flex-wrap justify-center gap-4">
                {isLoading ? (
                  // Skeleton loading for employee ranges
                  <>
                    <div className="w-40 h-14 bg-gray-200 rounded-md animate-pulse"></div>
                    <div className="w-40 h-14 bg-gray-200 rounded-md animate-pulse"></div>
                    <div className="w-40 h-14 bg-gray-200 rounded-md animate-pulse"></div>
                    <div className="w-40 h-14 bg-gray-200 rounded-md animate-pulse"></div>
                  </>
                ) : (
                  pricing.employeeRanges.map(range => {
                    // Get improved labels for range display
                    let rangeLabel = range.name;
                    
                    // Use friendly names based on range ID (assuming 1-4 are the IDs)
                    if (range.id === 1) {
                      rangeLabel = t("pricing.employeeRanges.small", "1 - 9 employés");
                    } else if (range.id === 2) {
                      rangeLabel = t("pricing.employeeRanges.medium", "10 - 49 employés");
                    } else if (range.id === 3) {
                      rangeLabel = t("pricing.employeeRanges.large", "50 - 250 employés");
                    } else if (range.id === 4) {
                      rangeLabel = t("pricing.employeeRanges.enterprise", "Plus de 250 employés");
                    }
                    
                    return (
                      <button
                        key={range.id}
                        onClick={() => setEmployeeRangeId(range.id)}
                        className={`px-5 py-3 rounded-md transition-colors min-w-40 ${
                          employeeRangeId === range.id 
                            ? 'bg-green-500 text-white font-medium' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {rangeLabel}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Partner count inputs */}
          {mode === "PARTNER" && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-center">
                {t("pricing.partnerCount")}
              </h2>
              
              {/* Always show the single supplier count input when in PARTNER mode */}
              <div className="flex justify-center mb-6">
                <div className="w-40">
                  <input
                    type="number"
                    id="supplierCount"
                    name="supplierCount"
                    min="1"
                    className={`w-full h-12 bg-white text-center text-xl border rounded-md ${
                      !isValid ? "border-red-500" : "border-gray-300 focus:border-green-500 focus:ring focus:ring-green-200"
                    }`}
                    value={supplierCount}
                    onChange={handleInputChange}
                    onClick={(e) => {
                      if (e.target instanceof HTMLInputElement) {
                        e.target.select();
                      }
                    }}
                  />
                </div>
              </div>

              {/* The 4 partner size breakdown inputs should only appear when RSE theme is selected in level 2 */}
              {shouldShowPartnerSizeBreakdown() && (
                <div>
                  <h3 className="text-lg font-medium mb-4 text-center">
                    {t("pricing.partnerSizeDistribution")}
                  </h3>
                  <div className="max-w-3xl mx-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                      {/* Partner size range inputs */}
                      {Object.entries(partnerSizeRanges).map(([range, value]) => (
                        <div key={range} className="bg-gray-50 p-4 rounded-lg">
                          <label className="font-medium text-sm mb-2 block text-gray-700">
                            {t(`pricing.partnerSize.${range}`, partnerSizeLabels[range as keyof typeof partnerSizeLabels])}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="1000"
                            className={`w-full h-10 bg-white text-center text-lg border rounded-md ${
                              !isPartnerSizeRangesValid[range as keyof typeof isPartnerSizeRangesValid] 
                                ? "border-red-500" 
                                : "border-gray-300 focus:border-green-500 focus:ring focus:ring-green-200"
                            }`}
                            value={value}
                            onChange={(e) => handlePartnerSizeRangeChange(range, e.target.value)}
                            onClick={(e) => {
                              if (e.target instanceof HTMLInputElement) {
                                e.target.select();
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Plans display */}
      <div className="max-w-5xl mx-auto mt-8 mb-12">
        <h2 className="text-xl font-semibold mb-8 text-center">{t("pricing.availablePlans")}</h2>
        <div>
        {isLoading ? (
          // Skeleton loading for plans
            <PricingSkeletonSection />
        ) : (
          <PricingTable
            plans={plans}
            calculatePrice={(plan) => {
              // Calculate final price based on all factors
              let finalPrice = plan.price || 0;
              
              // For partner plans
              if (plan?.type === "PARTNER") {
                finalPrice = calculatePartnerPrice(plan);
              }
              
              return finalPrice;
            }}
            onChoosePlan={onChoosePlan}
            isLoading={loadingPlan}
            getCombinationId={getCombinationId}
            mode={mode}
          />
        )}
        </div>
      </div>

      {/* <div className="max-w-4xl mx-auto text-center mb-16">
        <h1 className="font-bold text-xl mb-4">
          {t("home.headerFooterTitle")}
        </h1>
        <p className="text-lg">
          {t("home.headerFooter")}
        </p>
      </div> */}
    </div>
  );
}

interface ModeItemProps {
  name: string;
  mode: string;
  onClick: (mode: string) => void;
  isSelected: boolean;
}

function ModeItem({ name, mode, onClick, isSelected }: ModeItemProps) {
  return (
    <div
      onClick={() => onClick(mode)}
      className={`flex items-center justify-center cursor-pointer transition-all bg-white border rounded-md px-6 py-3 ${
        isSelected 
          ? "border-2 border-green-500 shadow-sm" 
          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <h1 className="font-semibold text-base text-black">{name}</h1>
    </div>
  );
}

interface ThemeItemProps {
  theme: Theme;
  onClick: (theme: Theme) => void;
  isSelcted: boolean;
  smallerWidth?: boolean;
}

function ThemeItem({ theme, onClick, isSelcted, smallerWidth }: ThemeItemProps) {
  return (
    <div
      onClick={() => onClick(theme)}
      className={`flex gap-3 items-center p-4 cursor-pointer border rounded-lg transition-all flex-none ${
        smallerWidth ? "w-52" : "w-64"
      } ${
        isSelcted 
          ? "border-green-500 bg-green-50" 
          : "border-gray-200 bg-white hover:bg-gray-50"
      }`}
    >
      <div
        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
          isSelcted ? "border-green-500 bg-green-500" : "border-gray-300"
        }`}
      >
        {isSelcted && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 12"
          >
            <path
              fill="none"
              stroke="#FFFFFF"
              strokeLinecap="round"
              strokeWidth="2"
              d="M2 6.542L6.645 10 14 2"
            />
          </svg>
        )}
      </div>
      <h1 className="font-medium text-lg">{theme.name}</h1>
    </div>
  );
}

interface PlanProps {
  plan: Plan;
  buttonText: string;
  onClick: (plan: Plan) => void;
  isLoading: boolean;
  totalToPay: number;
  code: number;
}

const Plan = ({ plan, buttonText, onClick, isLoading, totalToPay, code }: PlanProps) => {
  const [myValue, setMyValue] = useState("");
  const { t } = useTranslation("global");

  useEffect(() => {
    const updateMyValue = () => {
      const storedLangue = localStorage.getItem("langue");
      setMyValue(storedLangue || "");
    };

    updateMyValue();
    const intervalId = setInterval(updateMyValue, 1000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="flex-none w-80 flex flex-col bg-white rounded-lg shadow-md overflow-hidden transition-all hover:shadow-lg">
      {/* Plan header */}
      <div className="bg-gray-50 p-5 border-b border-gray-200">
        <h2 className="text-xl font-bold">{plan.name}</h2>
        <div className="text-3xl font-semibold text-gray-800 mt-2">{`€${totalToPay}`}</div>
      </div>
      
      {/* Plan body */}
      <div className="p-5 flex-grow">
        <ul className="space-y-3 mb-6">
          {plan.features.map((feature, index) => (
            <div key={index} className="flex items-center space-x-3">
              {!feature.disabled ? (
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100">
                  <svg
                    className="w-3 h-3 text-green-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              ) : (
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100">
                  <div className="w-3 h-0.5 bg-gray-400 rounded-full" />
                </div>
              )}
              <p
                className={`text-sm ${
                  !feature.disabled ? "text-gray-700" : "text-gray-400 line-through"
                }`}
              >
                {myValue === "French" ? feature?.name : feature?.en_name}
              </p>
            </div>
          ))}
        </ul>
      </div>
      
      {/* Plan footer */}
      <div className="p-5 border-t border-gray-200">
        <button
          disabled={isLoading}
          onClick={() => onClick(plan)}
          className="w-full flex justify-center items-center px-4 py-3 bg-green-500 text-white rounded-md transition-colors hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
        >
          {!isLoading && buttonText}
          {isLoading && (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t("pricing.loading")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PricingPage;
